import { computed } from 'vue';
import store, { AppPlatform } from '@/store'
import { AxiosInstance } from 'axios';
import { InfoModalMutations, ModalDataInterface } from '@/store/modules/infoModal';
import router from '@/router';
//Interfaces & Enums
import { ShareLinkInterface } from '@/types/interfaces';
import { AppColorsInterface } from '@/types/interfaces';
import { User, UserStatus } from '@/store/modules/user';
import { AssetInterface } from '@/types/v2/asset/interfaces';
import { TSecurityType } from '@brickwise/asset-validation';
//Other
import { FirebaseDynamicLinks } from '@pantrist/capacitor-firebase-dynamic-links';

export function checkUserAbilityToInvestByAddress(asset: AssetInterface, user: User) {
  const { isAllowedDomicile, isAllowedPlz } = asset.data.investmentDetails;
  const { domicile, PLZ } = user.personalInformation;

  if ((isAllowedDomicile && isAllowedDomicile.length > 0) || (isAllowedPlz && isAllowedPlz.length > 0)) {
  // User is allowed if domicile is not restricted OR if it is in the allowed list
  const domicileAllowed = isAllowedDomicile?.length === 0 ? true :  isAllowedDomicile?.includes(domicile.toUpperCase());
  // User is allowed if zip code is not restricted OR if it is in the allowed list
  const plzAllowed = isAllowedPlz?.length === 0 ? true : isAllowedPlz?.includes(PLZ);
  
  if (domicileAllowed && plzAllowed) {
    return false; // No error → user is allowed
  } else {
    return true;  // User not allowed
  }
} else {
  return false; // No restrictions defined → always allowed
}
}

export function checkUserAbilityToInvestInAustrianEquityAssets(status: string, asset: AssetInterface, user: User) {
  //Check if user are able to invest in Austrian Equity Assets (Must have Austrian TaxId)
  if(
    status !== 'sell' &&
    (asset.data.investmentDetails.securityType === TSecurityType.participationRightEquity || asset.data.investmentDetails.securityType === TSecurityType.participationRightEquityBuilderOwner) &&
    asset.data.investmentDetails.spv?.address.countryCode === 'AUT' &&
    (typeof user.account.taxIds === 'undefined' || user.account.taxIds.filter(item => item.country.toUpperCase() === 'AUT').length === 0)
  ) {
    return true
  } else {
    return false
  }
}

export function checkGermanTaxIdRequirement(status: string, asset: AssetInterface, user: User) {
  //We need german taxId:
  //1. if user is from Germany
  //2. user want's to invest in an asset that is located in Germany
  //3. User don't have stored German TaxID
  if(
    status === 'buy' &&
    asset.data.investmentDetails.spv?.address.countryCode.toUpperCase() === 'DEU' &&
    user.personalInformation.domicile.toUpperCase() === 'DEU' &&
    (typeof user.account.taxIds === 'undefined' || user.account.taxIds.filter(item => item.country.toUpperCase() === 'DEU').length === 0)
  ) {
    return true
  } else {
    return false
  }
}

export function resolveAppConfigColors(colors: AppColorsInterface) {
  const root = document.querySelector(':root') as HTMLElement
  const colorsList = {
    '--brand500': colors.brand.brand500,
    '--brand400': colors.brand.brand400,
    '--brand300': colors.brand.brand300,
    '--brand200': colors.brand.brand200,
    '--brand100': colors.brand.brand100,
    '--primary500': colors.primary.primary500,
    '--primary400': colors.primary.primary400,
    '--primary300': colors.primary.primary300,
    '--primary200': colors.primary.primary200,
    '--primary100': colors.primary.primary100,
    '--success500': colors.system.success500,
    '--success200': colors.system.success200,
    '--error500': colors.system.error500,
    '--error200': colors.system.error200,
    '--highlight500': colors.highlight.highlight500,
    '--highlight400': colors.highlight.highlight400,
    '--highlight300': colors.highlight.highlight300,
    '--highlight200': colors.highlight.highlight200,
    '--highlight100': colors.highlight.highlight100,
    '--navy900': colors.navyShades.navyShades900,
    '--navy800': colors.navyShades.navyShades800,
    '--navy700': colors.navyShades.navyShades700,
    '--navy600': colors.navyShades.navyShades600,
    '--navy500': colors.navyShades.navyShades500,
    '--navy400': colors.navyShades.navyShades400,
    '--navy300': colors.navyShades.navyShades300,
    '--navy200': colors.navyShades.navyShades200,
    '--navy100': colors.navyShades.navyShades100,
    '--white': colors.navyShades.white,
    '--primary-CTA-label-color': colors.CTAColors.primaryCTALabelColor,
  };

  Object.entries(colorsList).forEach(v => {
    root.style.setProperty(v[0], v[1])
  })

  // Set Favicon on desktop
  if (store.state.appPlatform !== AppPlatform.app) {
    const link = document.createElement("link"),
    oldLink = document.getElementById("dynamic-favicon");
    link.id = "dynamic-favicon";
    link.rel = "shortcut icon";
    link.href = store.state.appConfig.appIcon;
    if (oldLink) {
      document.head.removeChild(oldLink);
    }
    document.head.appendChild(link);
  }
}

export function resolveAppConfigFonts(font: string) {
  if(!font) return
  const root = document.querySelector(':root') as HTMLElement
  root.style.setProperty('--primaryFont', font)
}

export function resolvePartnerStyles(partnerId: string) {
  const root = document.querySelector(':root') as HTMLElement
  let stylesList = {
    '--assetCardShadow': "0px 4px 4px rgba(38, 49, 59, 0.04)",
    '--assetCardBorder': "1px solid var(--navy300)",
    '--borderRadius': "16px"
  }

  if(partnerId === "oekostrom") {
    stylesList = {
      '--assetCardShadow': "unset",
      '--assetCardBorder': "1px solid var(--navy200)",
      '--borderRadius': ".5em"
    }
  }

  Object.entries(stylesList).forEach(v => {
    root.style.setProperty(v[0], v[1])
  })
}

export const initPlatformPartnerId = (partnerId: string, instances: Array<AxiosInstance>) => {
  if (!partnerId) return;
  for(let i=0; i<instances.length;i++) {
    instances[i].interceptors.request.use(function (config: any) {
      const storage = computed(() => store.state.user)
      //@ts-ignore
      const userPlatformProvider = storage.value.data.platformProvider
      config.headers.partner_id = partnerId ? partnerId : userPlatformProvider;
      return config;
    });
  }
  console.log('Init Platform PartnerId', partnerId);
};

export function checkMonthForNextSpecificDay(day: any) {
  const date = new Date();
  const dt = date.getDate();
  date.setDate(day);
  if (dt >= day) {
    date.setMonth(date.getMonth() + 1);
  }

  return new Date(date).toLocaleDateString('de-DE')
}

export function endOfQuarterDate(date: any) {
  const t = new Date(date.getTime()), year = t.getFullYear()
  t.setHours(0, 0, 0, 0);
  return [3, 6, 9, 12].map(month => new Date(year, month, 0)).find(q=>t<=q)
}

export function endOfYearDate(currentYear: any) {
  return new Date(currentYear, 11, 31).toLocaleDateString('de-DE')
}

export const generateAssetShareLink = async (id: string): Promise<string> => {
  const appConfig = store.getters.getAppConfig

  const config: ShareLinkInterface = {
    domainUriPrefix: appConfig.domainUriPrefix,
    uri: `${appConfig.appURL}/asset/${id}`,
    androidParameters: {
      packageName: appConfig.appIdentifierAndroid
    },
    iosParameters: {
      bundleId: appConfig.appIdentifierIos,
      appStoreId: appConfig.appStoreId
    }
  };
  return FirebaseDynamicLinks.createDynamicShortLink(config).then(link => link.value);
}

export async function requestLogin(that: any) {
  function handleRegisterClick() {
    store.commit(InfoModalMutations.closeInfoModal)
    router.push(`/${store.state.appPlatform}/register`)
  }

  function handleLoginClick() {
    store.commit(InfoModalMutations.closeInfoModal)
    router.push(`/${store.state.appPlatform}/login`)
  }

  const data = {
    header: that.$t('Menu.login_popup_text'),
    buttons: [
      { type: 'primary', text: that.$t('Menu.login_popup_button1'), function: handleRegisterClick },
      { type: 'tertiary', text: that.$t('Menu.login_popup_button2'), function: handleLoginClick }
    ]
  } as ModalDataInterface

  store.commit(InfoModalMutations.openInfoModal, data)
}

// Buy/sell/payout readiness gate: opens an InfoModal that redirects the user to the
// Mangopay payment-account settings module until their account is ready. Pass a
// different descriptionKey (e.g. 'description_payout') for flow-specific wording.
export function requestMangopaySetup(that: any, descriptionKey = 'description') {
  function handleSetupClick() {
    store.commit(InfoModalMutations.closeInfoModal)
    router.push(`/${store.state.appPlatform}/settings?option=mangopayAccount`)
  }

  const data = {
    header: that.$t('MangopayReadinessGate.title'),
    descriptions: [
      { text: that.$t(`MangopayReadinessGate.${descriptionKey}`) }
    ],
    buttons: [
      { type: 'primary', text: that.$t('MangopayReadinessGate.button_setup'), function: handleSetupClick },
      { type: 'tertiary', text: that.$t('MangopayReadinessGate.button_cancel'), function: () => store.commit(InfoModalMutations.closeInfoModal) }
    ]
  } as ModalDataInterface

  store.commit(InfoModalMutations.openInfoModal, data)
}

// Shown at most once per app session: the helper below is called from every login path, so this
// guard prevents the reminder from popping on each of them (App.vue session-restore + the 3 login views).
let scaSetupReminderShown = false

/**
 * Proactive, non-blocking in-app reminder for ALREADY-ACTIVE users who still need to finish their
 * Mangopay payment setup / SCA (the extra security verification Mangopay now requires). This is the
 * "notify already-active users to finish SCA in the app" strategy (no push): shown once per session
 * on login, only for partners on the in-app payment flow, and only when the persisted readiness says
 * the account is not ready yet. Tapping "set up now" deep-links to the Mangopay settings module; once
 * the user finishes SCA there, readiness is re-fetched (module refresh on open + Mangopay webhooks).
 */
export function maybePromptFinishSca(that: any) {
  if (scaSetupReminderShown) return
  // Only relevant for Mangopay partners (= not manualPayment). NOTE: brickwiseAppPayment does
  // not exist on the appconfig docs — gating on it would mean this never fires.
  if (store.state.appConfig?.featureFlags?.manualPayment) return
  const user = store.state.user?.data
  // Only nudge users who are already active (onboarding finished) but whose payment account isn't ready.
  if (user?.status !== UserStatus.active) return
  if (!user?.mangopayReadiness || user.mangopayReadiness.isReady) return

  scaSetupReminderShown = true

  function handleSetupClick() {
    store.commit(InfoModalMutations.closeInfoModal)
    router.push(`/${store.state.appPlatform}/settings?option=mangopayAccount`)
  }

  const data = {
    header: that.$t('ScaReminder.title'),
    descriptions: [
      { text: that.$t('ScaReminder.description') }
    ],
    buttons: [
      { type: 'primary', text: that.$t('ScaReminder.button_setup'), function: handleSetupClick },
      { type: 'tertiary', text: that.$t('ScaReminder.button_cancel'), function: () => store.commit(InfoModalMutations.closeInfoModal) }
    ]
  } as ModalDataInterface

  store.commit(InfoModalMutations.openInfoModal, data)
}
